import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '../../components/layout/MainLayout';
import { vehicleService } from '../../api/services/vehicleService';
import { Loader2, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

// ── Types ────────────────────────────────────────────────────────────────────

type ProductType = 'seat-covers' | 'cargo-liner' | 'dash-cover';

interface DropdownOption {
  Id: number;
  Name: string;
}

interface AppFinderQA {
  QuestionId: number;
  Question: string;
  Answer: string;
}

interface AppFinderPart {
  PartId: number;
  PartNumber: string;
  SeatType: string;
  SeatRowCode: string;
  Image: string;
  Extras: string;
  QuestionAnswers: AppFinderQA[];
}

interface AppFinderData {
  vehicleDesc: string;
  FrontParts?: AppFinderPart[];
  MiddleParts?: AppFinderPart[];
  RearParts?: AppFinderPart[];
  CargoParts: AppFinderPart[];
  DashParts?: AppFinderPart[];
}

interface QuestionRow {
  questionId: number;
  question: string;
  answers: Record<string, string>;
  highlight: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildQuestionTable(parts: AppFinderPart[]): QuestionRow[] {
  const questionOrder: number[] = [];
  const questionTexts: Record<number, string> = {};
  for (const part of parts) {
    for (const qa of part.QuestionAnswers) {
      if (!(qa.QuestionId in questionTexts)) {
        questionOrder.push(qa.QuestionId);
        questionTexts[qa.QuestionId] = qa.Question;
      }
    }
  }

  const rows = questionOrder.map(qId => {
    const answers: Record<string, string> = {};
    for (const part of parts) {
      const qa = part.QuestionAnswers.find(q => q.QuestionId === qId);
      answers[part.PartNumber] = qa?.Answer ?? '';
    }
    const uniqueValues = new Set(Object.values(answers).filter(Boolean));
    const highlight = uniqueValues.size > 1;
    return { questionId: qId, question: questionTexts[qId], answers, highlight };
  });

  return [...rows.filter(r => r.highlight), ...rows.filter(r => !r.highlight)];
}

// ── Sub-component: Section Table ─────────────────────────────────────────────

interface SectionTableProps {
  title: string;
  parts: AppFinderPart[];
  imageBasePath: string;
}

const SectionTable: React.FC<SectionTableProps> = ({ title, parts, imageBasePath }) => {
  const rows = buildQuestionTable(parts);

  return (
    <div className="mb-8">
      <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">{title}</h4>

      {parts.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">N/A</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-auto mx-auto text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="p-3 text-left font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 border-r border-gray-200 dark:border-gray-700 min-w-[180px] align-bottom">
                  Question
                </th>
                {parts.map(part => (
                  <th
                    key={part.PartNumber}
                    className="p-3 text-center bg-gray-50 dark:bg-gray-800/60 border-r border-gray-200 dark:border-gray-700 last:border-r-0 min-w-[210px]"
                  >
                    <div className="font-bold text-gray-900 dark:text-gray-100 text-base">
                      {part.PartNumber}
                    </div>
                    {part.SeatType && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {part.SeatType}
                      </div>
                    )}
                    {part.Extras && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 italic">
                        {part.Extras}
                      </div>
                    )}
                    {part.Image && (
                      <div className="mt-2 flex justify-center">
                        <img
                          src={`${imageBasePath}/${part.Image}.jpg`}
                          alt={part.Image}
                          className="max-w-[200px] max-h-[150px] object-contain"
                          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={parts.length + 1}
                    className="p-3 text-center text-gray-400 dark:text-gray-500 italic"
                  >
                    No specifications available
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr
                    key={row.questionId}
                    className={[
                      'border-t border-gray-200 dark:border-gray-700',
                      row.highlight
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : idx % 2 === 0
                          ? 'bg-white dark:bg-gray-900'
                          : 'bg-gray-50/60 dark:bg-gray-800/30',
                    ].join(' ')}
                  >
                    <td className="p-3 text-gray-700 dark:text-gray-300 font-medium border-r border-gray-200 dark:border-gray-700">
                      {row.question}
                      {row.highlight && (
                        <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-blue-400 align-middle" />
                      )}
                    </td>
                    {parts.map(part => (
                      <td
                        key={part.PartNumber}
                        className="p-3 text-center text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                      >
                        {row.answers[part.PartNumber] || '—'}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

function parseProductType(raw: string | null): ProductType {
  return raw === 'cargo-liner' || raw === 'dash-cover' ? raw : 'seat-covers';
}

function parseIntParam(raw: string | null): number | null {
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? null : n;
}

const AppFinder: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [productType, setProductType] = useState<ProductType>(() => parseProductType(searchParams.get('type')));

  // Cascade selections — initialized from the URL so a shared/bookmarked link
  // (or toggling product type) can restore a prior selection.
  const [selectedYear,      setSelectedYear]      = useState<number | null>(() => parseIntParam(searchParams.get('year')));
  const [selectedMakeId,    setSelectedMakeId]    = useState<number | null>(() => parseIntParam(searchParams.get('makeId')));
  const [selectedModelId,   setSelectedModelId]   = useState<number | null>(() => parseIntParam(searchParams.get('modelId')));
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(() => parseIntParam(searchParams.get('vehicleId')));

  // Dropdown data
  const [years,          setYears]          = useState<number[]>([]);
  const [makes,          setMakes]          = useState<DropdownOption[]>([]);
  const [models,         setModels]         = useState<DropdownOption[]>([]);
  const [vehicleOptions, setVehicleOptions] = useState<DropdownOption[]>([]);
  const [partsData,      setPartsData]      = useState<AppFinderData | null>(null);

  // Loading flags
  const [loadingYears,   setLoadingYears]   = useState(false);
  const [loadingMakes,   setLoadingMakes]   = useState(false);
  const [loadingModels,  setLoadingModels]  = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingParts,   setLoadingParts]   = useState(false);

  const isCargo = productType === 'cargo-liner';
  const isDash = productType === 'dash-cover';

  // ── Sync selections to the URL ──────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('type', productType);
    if (selectedYear)      params.set('year', String(selectedYear));
    if (selectedMakeId)    params.set('makeId', String(selectedMakeId));
    if (selectedModelId)   params.set('modelId', String(selectedModelId));
    if (selectedVehicleId) params.set('vehicleId', String(selectedVehicleId));
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType, selectedYear, selectedMakeId, selectedModelId, selectedVehicleId]);

  // ── Fetch part data for a vehicle (explicit product type avoids stale-state races) ──
  const fetchPartsData = useCallback(async (vehicleId: number, type: ProductType) => {
    setSelectedVehicleId(vehicleId);
    setPartsData(null);
    setLoadingParts(true);
    try {
      const data = type === 'cargo-liner'
        ? await vehicleService.getCargoAppFinderPartsData(vehicleId)
        : type === 'dash-cover'
        ? await vehicleService.getDashAppFinderPartsData(vehicleId)
        : await vehicleService.getAppFinderPartsData(vehicleId);
      setPartsData(data);
    } catch {
      toast.error('Failed to load part data');
    } finally {
      setLoadingParts(false);
    }
  }, []);

  // ── Re-derive the cascade for a (possibly new) product type, re-using as much
  //    of the prior selection as still applies. Year/Make/Model IDs come from the
  //    shared VCdb tables so they carry over directly; only the vehicle-option Id
  //    space differs per product type (VehicleApplication vs Cargo/DashVehicleLink),
  //    so we re-fetch that step and try to match the previous body style by name. ──
  const loadCascadeForType = useCallback(async (
    type: ProductType,
    year: number | null,
    makeId: number | null,
    modelId: number | null,
    vehicleId: number | null,
    vehicleNameHint: string | null,
  ) => {
    if (!year) return;
    const cargo = type === 'cargo-liner';
    const dash = type === 'dash-cover';

    setLoadingMakes(true);
    let makesData: DropdownOption[] = [];
    try {
      makesData = cargo
        ? await vehicleService.getCargoAppFinderMakes(year)
        : dash
        ? await vehicleService.getDashAppFinderMakes(year)
        : await vehicleService.getAppFinderMakes(year);
      setMakes(makesData);
    } catch {
      toast.error('Failed to load makes');
    } finally {
      setLoadingMakes(false);
    }
    if (!makeId || !makesData.some(m => m.Id === makeId)) {
      setSelectedMakeId(null);
      return;
    }
    setSelectedMakeId(makeId);

    setLoadingModels(true);
    let modelsData: DropdownOption[] = [];
    try {
      modelsData = cargo
        ? await vehicleService.getCargoAppFinderModels(year, makeId)
        : dash
        ? await vehicleService.getDashAppFinderModels(year, makeId)
        : await vehicleService.getAppFinderModels(year, makeId);
      setModels(modelsData);
    } catch {
      toast.error('Failed to load models');
    } finally {
      setLoadingModels(false);
    }
    if (!modelId || !modelsData.some(m => m.Id === modelId)) {
      setSelectedModelId(null);
      return;
    }
    setSelectedModelId(modelId);

    setLoadingOptions(true);
    let optionsData: DropdownOption[] = [];
    try {
      optionsData = cargo
        ? await vehicleService.getCargoAppFinderVehicleOptions(year, makeId, modelId)
        : dash
        ? await vehicleService.getDashAppFinderVehicleOptions(year, makeId, modelId)
        : await vehicleService.getAppFinderVehicleOptions(year, makeId, modelId);
      setVehicleOptions(optionsData);
    } catch {
      toast.error('Failed to load vehicle options');
    } finally {
      setLoadingOptions(false);
    }

    let matchId: number | null = null;
    if (vehicleId && optionsData.some(v => v.Id === vehicleId)) {
      matchId = vehicleId;
    } else if (vehicleNameHint) {
      matchId = optionsData.find(v => v.Name === vehicleNameHint)?.Id ?? null;
    }
    if (!matchId && optionsData.length === 1) {
      matchId = optionsData[0].Id;
    }

    if (matchId) {
      fetchPartsData(matchId, type);
    } else {
      setSelectedVehicleId(null);
      setPartsData(null);
    }
  }, [fetchPartsData]);

  const handleProductTypeChange = useCallback((type: ProductType) => {
    if (type === productType) return;
    const vehicleNameHint = vehicleOptions.find(v => v.Id === selectedVehicleId)?.Name ?? null;
    setProductType(type);
    setVehicleOptions([]);
    setPartsData(null);
    setSelectedVehicleId(null);
    loadCascadeForType(type, selectedYear, selectedMakeId, selectedModelId, selectedVehicleId, vehicleNameHint);
  }, [productType, selectedYear, selectedMakeId, selectedModelId, selectedVehicleId, vehicleOptions, loadCascadeForType]);

  // ── Hydrate the cascade from the URL on first load ──────────────────────────
  useEffect(() => {
    if (selectedYear) {
      loadCascadeForType(productType, selectedYear, selectedMakeId, selectedModelId, selectedVehicleId, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load years when product type changes ───────────────────────────────────
  useEffect(() => {
    setLoadingYears(true);
    const fetch = isCargo
      ? vehicleService.getCargoAppFinderYears()
      : isDash
      ? vehicleService.getDashAppFinderYears()
      : vehicleService.getAppFinderYears();
    fetch
      .then(setYears)
      .catch(() => toast.error('Failed to load years'))
      .finally(() => setLoadingYears(false));
  }, [isCargo, isDash]);

  // ── Year change ────────────────────────────────────────────────────────────
  const handleYearChange = useCallback(async (raw: string) => {
    const year = raw ? parseInt(raw) : null;
    setSelectedYear(year);
    setSelectedMakeId(null);
    setSelectedModelId(null);
    setSelectedVehicleId(null);
    setMakes([]);
    setModels([]);
    setVehicleOptions([]);
    setPartsData(null);
    if (!year) return;

    setLoadingMakes(true);
    try {
      const data = isCargo
        ? await vehicleService.getCargoAppFinderMakes(year)
        : isDash
        ? await vehicleService.getDashAppFinderMakes(year)
        : await vehicleService.getAppFinderMakes(year);
      setMakes(data);
    } catch {
      toast.error('Failed to load makes');
    } finally {
      setLoadingMakes(false);
    }
  }, [isCargo, isDash]);

  // ── Make change ────────────────────────────────────────────────────────────
  const handleMakeChange = useCallback(async (raw: string) => {
    const makeId = raw ? parseInt(raw) : null;
    setSelectedMakeId(makeId);
    setSelectedModelId(null);
    setSelectedVehicleId(null);
    setModels([]);
    setVehicleOptions([]);
    setPartsData(null);
    if (!makeId || !selectedYear) return;

    setLoadingModels(true);
    try {
      const data = isCargo
        ? await vehicleService.getCargoAppFinderModels(selectedYear, makeId)
        : isDash
        ? await vehicleService.getDashAppFinderModels(selectedYear, makeId)
        : await vehicleService.getAppFinderModels(selectedYear, makeId);
      setModels(data);
    } catch {
      toast.error('Failed to load models');
    } finally {
      setLoadingModels(false);
    }
  }, [selectedYear, isCargo, isDash]);

  // ── Vehicle select ─────────────────────────────────────────────────────────
  const handleVehicleSelect = useCallback((vehicleId: number) => {
    fetchPartsData(vehicleId, productType);
  }, [fetchPartsData, productType]);

  const handleVehicleChange = useCallback((raw: string) => {
    const vehicleId = raw ? parseInt(raw) : null;
    if (!vehicleId) {
      setSelectedVehicleId(null);
      setPartsData(null);
      return;
    }
    handleVehicleSelect(vehicleId);
  }, [handleVehicleSelect]);

  // ── Model change ───────────────────────────────────────────────────────────
  const handleModelChange = useCallback(async (raw: string) => {
    const modelId = raw ? parseInt(raw) : null;
    setSelectedModelId(modelId);
    setSelectedVehicleId(null);
    setVehicleOptions([]);
    setPartsData(null);
    if (!modelId || !selectedYear || !selectedMakeId) return;

    setLoadingOptions(true);
    try {
      const data = isCargo
        ? await vehicleService.getCargoAppFinderVehicleOptions(selectedYear, selectedMakeId, modelId)
        : isDash
        ? await vehicleService.getDashAppFinderVehicleOptions(selectedYear, selectedMakeId, modelId)
        : await vehicleService.getAppFinderVehicleOptions(selectedYear, selectedMakeId, modelId);
      setVehicleOptions(data);
      if (data.length === 1) {
        handleVehicleSelect(data[0].Id);
      }
    } catch {
      toast.error('Failed to load vehicle options');
    } finally {
      setLoadingOptions(false);
    }
  }, [selectedYear, selectedMakeId, handleVehicleSelect, isCargo, isDash]);

  // ── Breadcrumb label helpers ───────────────────────────────────────────────
  const selectedMakeName    = makes.find(m => m.Id === selectedMakeId)?.Name    ?? '';
  const selectedModelName   = models.find(m => m.Id === selectedModelId)?.Name  ?? '';
  const selectedVehicleName = vehicleOptions.find(v => v.Id === selectedVehicleId)?.Name ?? '';

  const anyLoading = loadingYears || loadingMakes || loadingModels || loadingOptions || loadingParts;

  // ── Results sections ───────────────────────────────────────────────────────
  const resultSections = partsData
    ? isCargo
      ? [{ title: 'Cargo Liner Parts', parts: partsData.CargoParts ?? [] }].filter(s => s.parts.length > 0)
      : isDash
      ? [{ title: 'Dash Cover Parts', parts: partsData.DashParts ?? [] }].filter(s => s.parts.length > 0)
      : [
          { title: 'Front Styles',  parts: partsData.FrontParts  ?? [] },
          { title: 'Middle Styles', parts: partsData.MiddleParts ?? [] },
          { title: 'Rear Styles',   parts: partsData.RearParts   ?? [] },
          { title: 'Cargo Items',   parts: partsData.CargoParts  ?? [] },
        ].filter(s => s.parts.length > 0)
    : [];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0">
        {/* Header card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">App Finder</h2>

            <div className="flex items-center gap-3">
              {anyLoading && (
                <Loader2 size={18} className="animate-spin text-blue-500" />
              )}
              {/* Product type toggle */}
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden text-sm font-medium">
                <button
                  onClick={() => handleProductTypeChange('seat-covers')}
                  className={[
                    'px-4 py-1.5 transition-colors',
                    productType === 'seat-covers'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600',
                  ].join(' ')}
                >
                  Seat Covers
                </button>
                <button
                  onClick={() => handleProductTypeChange('cargo-liner')}
                  className={[
                    'px-4 py-1.5 border-l border-gray-200 dark:border-gray-600 transition-colors',
                    productType === 'cargo-liner'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600',
                  ].join(' ')}
                >
                  Cargo Liner
                </button>
                <button
                  onClick={() => handleProductTypeChange('dash-cover')}
                  className={[
                    'px-4 py-1.5 border-l border-gray-200 dark:border-gray-600 transition-colors',
                    productType === 'dash-cover'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600',
                  ].join(' ')}
                >
                  Dash Cover
                </button>
              </div>
            </div>
          </div>

          {/* Cascade dropdowns */}
          <div className="px-6 py-4">
            <div className="flex flex-wrap items-end gap-4">

              {/* Year */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Year
                </label>
                <select
                  value={selectedYear ?? ''}
                  onChange={e => handleYearChange(e.target.value)}
                  disabled={loadingYears}
                  className="w-28 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">[Year]</option>
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {/* Make */}
              {selectedYear && (
                <>
                  <ChevronRight size={16} className="text-gray-400 mb-2 hidden sm:block" />
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Make
                    </label>
                    <select
                      value={selectedMakeId ?? ''}
                      onChange={e => handleMakeChange(e.target.value)}
                      disabled={loadingMakes}
                      className="w-40 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <option value="">[Select Make]</option>
                      {makes.map(m => (
                        <option key={m.Id} value={m.Id}>{m.Name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Model */}
              {selectedMakeId && (
                <>
                  <ChevronRight size={16} className="text-gray-400 mb-2 hidden sm:block" />
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Model
                    </label>
                    <select
                      value={selectedModelId ?? ''}
                      onChange={e => handleModelChange(e.target.value)}
                      disabled={loadingModels}
                      className="w-44 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <option value="">[Select Model]</option>
                      {models.map(m => (
                        <option key={m.Id} value={m.Id}>{m.Name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Body / Sub Model */}
              {selectedModelId && vehicleOptions.length > 1 && (
                <>
                  <ChevronRight size={16} className="text-gray-400 mb-2 hidden sm:block" />
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Body Type / Sub Model
                    </label>
                    <select
                      value={selectedVehicleId ?? ''}
                      onChange={e => handleVehicleChange(e.target.value)}
                      disabled={loadingOptions}
                      className="w-64 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <option value="">[Select Body Type]</option>
                      {vehicleOptions.map(v => (
                        <option key={v.Id} value={v.Id}>{v.Name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {/* Breadcrumb trail */}
            {selectedYear && (
              <div className="mt-3 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 flex-wrap">
                <span className="font-medium text-gray-600 dark:text-gray-300">{selectedYear}</span>
                {selectedMakeName && (
                  <>
                    <ChevronRight size={12} />
                    <span className="font-medium text-gray-600 dark:text-gray-300">{selectedMakeName}</span>
                  </>
                )}
                {selectedModelName && (
                  <>
                    <ChevronRight size={12} />
                    <span className="font-medium text-gray-600 dark:text-gray-300">{selectedModelName}</span>
                  </>
                )}
                {selectedVehicleName && (
                  <>
                    <ChevronRight size={12} />
                    <span className="font-medium text-gray-600 dark:text-gray-300">{selectedVehicleName}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Loading parts spinner */}
        {loadingParts && (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={32} className="animate-spin text-blue-500" />
            <span className="ml-3 text-gray-500 dark:text-gray-400">Loading applications…</span>
          </div>
        )}

        {/* Results */}
        {partsData && !loadingParts && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-center text-gray-900 dark:text-gray-100">
                {partsData.vehicleDesc}
              </h3>
              <div className="mt-2 flex items-center justify-center gap-4 text-xs text-gray-400 dark:text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm bg-blue-100 dark:bg-blue-900/40 border border-blue-200" />
                  Rows with differing options
                </span>
              </div>
            </div>

            <div className="p-6 space-y-2">
              {resultSections.length === 0 ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-8">
                  No part applications found for this vehicle.
                </p>
              ) : (
                resultSections.map((s, i) => (
                  <React.Fragment key={s.title}>
                    {i > 0 && <hr className="border-gray-200 dark:border-gray-700" />}
                    <SectionTable title={s.title} parts={s.parts} imageBasePath={isDash ? '/dash-diagrams' : '/seat-diagrams'} />
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
        )}

        {/* Empty state — model selected but no vehicle chosen yet */}
        {selectedModelId && vehicleOptions.length > 1 && !selectedVehicleId && !loadingParts && (
          <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-500">
            Select a body type / sub model to view applications
          </div>
        )}

        {/* Empty state — model selected, 0 options */}
        {selectedModelId && vehicleOptions.length === 0 && !loadingOptions && !loadingParts && (
          <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-500">
            No vehicle applications found for this selection
          </div>
        )}

        {/* Initial state */}
        {!selectedYear && !loadingYears && (
          <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-500">
            Select a year to begin
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AppFinder;
